/**
 * Normalizes an image File to a guaranteed real JPEG, resized and
 * compressed to keep every photo comfortably under S3's 5MB multipart
 * chunk size — so a photo always uploads as a single part, regardless of
 * connection quality (1E.3's multipart resilience work targets multi-part
 * uploads; keeping photos single-part sidesteps that class of problem for
 * this media type entirely, rather than depending on it).
 *
 * ALWAYS runs now, regardless of the original file's type — including
 * files that already arrive as JPEG. This used to be skipped for
 * already-JPEG files (on the reasoning "nothing to normalize"), but that
 * also skipped resizing/compression, letting an unprocessed multi-
 * megabyte phone-camera JPEG through completely untouched. Also protects
 * against files that arrive with a misleading extension/type (most
 * commonly iPhone HEIC photos that a non-Safari browser, or an in-app
 * WebView, hands off without converting).
 *
 * Relies on the browser's own <img> decoder. Safari can decode HEIC this
 * way; most other browsers cannot — in that case this rejects, and the
 * caller should show the person a clear error rather than silently
 * uploading bytes that will never render.
 *
 * Resize + compress strategy: downscale to at most MAX_DIMENSION on the
 * long edge (never upscale — a small original stays small), then try
 * JPEG quality levels from QUALITY_STEPS in order, stopping at the first
 * one that lands under TARGET_MAX_BYTES. If even the lowest quality step
 * is still over target (rare — a very large or unusually noisy/
 * incompressible image), accepts that result rather than degrading
 * further or failing the upload outright — best-effort, matching this
 * codebase's general philosophy elsewhere (never leave the person stuck
 * over an edge case that isn't their fault).
 *
 * @param {File} file
 * @returns {Promise<File>} a new File, always image/jpeg
 */

const MAX_DIMENSION = 2048; // long-edge cap, px — ample for this app's gallery/lightbox display, comfortably beyond typical viewing size even on retina screens
const TARGET_MAX_BYTES = 2 * 1024 * 1024; // 2MB — real margin under the 5MB multipart chunk size, not a near-miss
const QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5];

// Pure — testable without a browser. Never upscales; returns the original
// dimensions unchanged if already within the cap.
export function computeScaledDimensions(naturalWidth, naturalHeight, maxDimension = MAX_DIMENSION) {
    const longEdge = Math.max(naturalWidth, naturalHeight);
    if (longEdge <= maxDimension) {
        return { width: naturalWidth, height: naturalHeight };
    }
    const scale = maxDimension / longEdge;
    return {
        width: Math.round(naturalWidth * scale),
        height: Math.round(naturalHeight * scale),
    };
}

function canvasToBlob(canvas, quality) {
    return new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
    });
}

function loadImage(objectUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('DECODE_FAILED'));
        img.src = objectUrl;
    });
}

export async function convertImageToJpeg(file) {
    const objectUrl = URL.createObjectURL(file);

    try {
        let img;
        try {
            img = await loadImage(objectUrl);
        } catch (err) {
            throw new Error(`Could not process "${file.name}" — the browser could not decode this image. Try a JPEG or PNG instead.`);
        }

        const { width, height } = computeScaledDimensions(img.naturalWidth, img.naturalHeight);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // JPEG has no alpha channel. A freshly created canvas starts
        // fully transparent (0,0,0,0) — without an explicit fill,
        // exporting to JPEG drops the alpha and keeps the RGB values
        // as-is, which for any transparent region are (0,0,0): a
        // transparent PNG silently becomes solid BLACK where it should
        // be see-through. Filling white first (matching the conventional
        // default most image tools use for this same conversion) means
        // transparent regions resolve to white instead.
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        let blob = null;
        for (const quality of QUALITY_STEPS) {
            blob = await canvasToBlob(canvas, quality);
            if (!blob) {
                throw new Error(`Could not process "${file.name}" — the browser could not decode this image.`);
            }
            if (blob.size <= TARGET_MAX_BYTES) break;
            // Still too large — fall through and try the next (lower)
            // quality step, keeping this attempt as the current best in
            // case it turns out to be the last one.
        }

        const jpegName = file.name.replace(/\.\w+$/, '') + '.jpg';
        return new File([blob], jpegName, { type: 'image/jpeg' });
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}